import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import './ProgressSummaryPage.css';

// Import SVG icons for goal tags - matching reference project exactly
import { ReactComponent as Cog2xIcon } from '../../../assets/icons/goal-tag/cog-2x.svg';
import { ReactComponent as Gm2xIcon } from '../../../assets/icons/goal-tag/gm-2x.svg';
import { ReactComponent as Fm2xIcon } from '../../../assets/icons/goal-tag/fm-2x.svg';
import { ReactComponent as Pw2xIcon } from '../../../assets/icons/goal-tag/pw-2x.svg';
import { ReactComponent as Sh2xIcon } from '../../../assets/icons/goal-tag/sh-2x.svg';
import { ReactComponent as Sl2xIcon } from '../../../assets/icons/goal-tag/sl-2x.svg';
import { ReactComponent as Ps2xIcon } from '../../../assets/icons/goal-tag/ps-2x.svg';
import { ReactComponent as Se2xIcon } from '../../../assets/icons/goal-tag/se-2x.svg';

// Import back arrow icon
import { ReactComponent as ArrowLeftIcon } from '../../../assets/icons/arrow-left.svg';

// Import Redux selectors and actions
import { CurrentSettingsSelectors } from '../../../redux/CurrentSettingsRedux';
import { GetChildrenSelectors } from '../../../redux/GetChildrenRedux';
import GetChildrenActions from '../../../redux/GetChildrenRedux';
import { CurrentSettingsActions } from '../../../redux/CurrentSettingsRedux';

interface ProgressSummaryPageProps extends WithTranslation {
  navigate?: (path: string) => void;
  goBack?: () => void;
  // Redux props
  currentSettings?: any;
  children?: any[];
  getChildrenFetching?: boolean;
  getChildrenSuccess?: boolean;
  getChildrenError?: boolean;
  // Actions
  getChildren?: () => void;
  setCurrentChild?: (child: any) => void;
}

interface ProgressSummaryPageState {
  childName: string;
  stats: {
    cog?: number;
    gm?: number;
    fm?: number;
    pw?: number;
    sh?: number;
    sl?: number;
    ps?: number;
    se?: number;
  };
  chartUrl: string;
  loading: boolean;
  error: string | null;
}

class ProgressSummaryPage extends Component<ProgressSummaryPageProps, ProgressSummaryPageState> {
  constructor(props: ProgressSummaryPageProps) {
    super(props);
    
    // Initialize state with data from props
    const child = props.currentSettings?.child;
    const summary = child?.progress_summary;
    
    this.state = {
      childName: child?.nickname || 'Child',
      stats: summary?.goals_stats || {},
      chartUrl: summary?.chart_url || '',
      loading: !child || !summary,
      error: null,
    };
  }

  componentDidMount() {
    console.log('ProgressSummaryPage mounted');
    console.log('Current settings:', this.props.currentSettings);
    console.log('Children data:', this.props.children);
    
    // Add browser back button listener
    window.addEventListener('popstate', this.handleBrowserBack);
    
    // Check if we need to load children data
    this.checkAndLoadData();
  }

  componentWillUnmount() {
    // Cleanup browser back button listener
    window.removeEventListener('popstate', this.handleBrowserBack);
  }

  componentDidUpdate(prevProps: ProgressSummaryPageProps) {
    // Update state when props change
    if (prevProps.currentSettings !== this.props.currentSettings) {
      const child = this.props.currentSettings?.child;
      const summary = child?.progress_summary;
      
      this.setState({
        childName: child?.nickname || 'Child',
        stats: summary?.goals_stats || {},
        chartUrl: summary?.chart_url || '',
        loading: !child || !summary,
        error: null,
      });
    }

    // Handle children data updates
    if (prevProps.children !== this.props.children && this.props.children && this.props.children.length > 0) {
      this.handleChildrenDataUpdate();
    }
  }

  // Check if we need to load data and handle data loading
  checkAndLoadData = () => {
    const { currentSettings, children, getChildren } = this.props;
    
    // If we have current child data, no need to load
    if (currentSettings?.child) {
      console.log('Current child data available:', currentSettings.child);
      return;
    }
    
    // If we have children data but no current child, set the first one
    if (children && children.length > 0) {
      console.log('Children data available, setting first child as current');
      this.handleChildrenDataUpdate();
      return;
    }
    
    // If no data available, try to load children
    if (getChildren && !this.props.getChildrenFetching) {
      console.log('No data available, loading children...');
      getChildren();
    }
  };

  // Handle children data update
  handleChildrenDataUpdate = () => {
    const { children, setCurrentChild } = this.props;
    
    if (children && children.length > 0 && setCurrentChild) {
      const firstChild = children[0];
      console.log('Setting first child as current:', firstChild);
      setCurrentChild(firstChild);
      
      // Update local state
      const summary = firstChild.progress_summary;
      this.setState({
        childName: firstChild.nickname || firstChild.name || 'Child',
        stats: summary?.goals_stats || {},
        chartUrl: summary?.chart_url || '',
        loading: !summary,
        error: null,
      });
    }
  };

  handleBrowserBack = () => {
    this.back();
  };

  back = () => {
    if (this.props.goBack) {
      this.props.goBack();
    } else if (this.props.navigate) {
      this.props.navigate('/goals');
    } else {
      // Fallback to browser back
      window.history.back();
    }
  };

  // Render goal tag item with icon and count
  renderGoalTagItem = (tag: string, count: number, icon: React.ComponentType<any>) => {
    if (!count) return null;

    const IconComponent = icon;
    const tagKey = tag.toLowerCase();
    
    // Goal tag colors matching reference project
    const tagColors: { [key: string]: string } = {
      cog: '#2AC443', // Cognitive - Green
      gm: '#F68121',  // Gross Motor - Orange
      fm: '#FB6240',  // Fine Motor - Red
      pw: '#FED700',  // Pre-Writing - Yellow
      sh: '#2BD5CA',  // Self-Help - Teal
      sl: '#8D5DA6',  // Speech & Language - Purple
      ps: '#E583D9',  // Personal-Social - Pink
      se: '#007AFF',  // Social-Emotional - Blue
    };

    const backgroundColor = tagColors[tagKey] || '#808284';

    return (
      <div 
        key={tag}
        className="goal-tag-item"
        style={{ backgroundColor }}
      >
        <div className="goal-tag-title">
          {this.props.t(`progress-summary-screen:tag.${tagKey}`)}
        </div>
        <div className="goal-tag-right">
          <IconComponent className="goal-tag-icon" />
          <span className="goal-tag-count">{count}</span>
        </div>
      </div>
    );
  };

  // Render progress chart - PHASE 4 IMPLEMENTED
  renderProgressChart = () => {
    const { chartUrl, loading } = this.state;

    if (loading) {
      return (
        <div className="chart-loading">
          <div className="loading-spinner">Loading chart...</div>
        </div>
      );
    }

    if (!chartUrl) {
      return (
        <div className="chart-no-data">
          <p>No progress chart available</p>
        </div>
      );
    }

    // Use iframe for external chart URL - PHASE 4 COMPLETE
    return (
      <div className="chart-container">
        <iframe
          src={chartUrl}
          className="progress-chart"
          title="Progress Chart"
          frameBorder="0"
          scrolling="no"
          allowFullScreen
        />
      </div>
    );
  };

  // Render goal attempts section
  renderGoalAttempts = () => {
    const { stats } = this.state;
    const hasStats = Object.values(stats).some(count => count && count > 0);

    if (!hasStats) {
      return (
        <div className="no-attempts">
          <p>{this.props.t("progress-summary-screen:no-attempts")}</p>
        </div>
      );
    }

    return (
      <div className="goal-attempts-section">
        <h2 className="section-header uppercase">
          {this.props.t("progress-summary-screen:section.title.number-of-attempts")}
        </h2>
        <div className="goal-tags-list">
          {this.renderGoalTagItem('cog', stats.cog || 0, Cog2xIcon)}
          {this.renderGoalTagItem('gm', stats.gm || 0, Gm2xIcon)}
          {this.renderGoalTagItem('fm', stats.fm || 0, Fm2xIcon)}
          {this.renderGoalTagItem('pw', stats.pw || 0, Pw2xIcon)}
          {this.renderGoalTagItem('sh', stats.sh || 0, Sh2xIcon)}
          {this.renderGoalTagItem('sl', stats.sl || 0, Sl2xIcon)}
          {this.renderGoalTagItem('ps', stats.ps || 0, Ps2xIcon)}
          {this.renderGoalTagItem('se', stats.se || 0, Se2xIcon)}
        </div>
      </div>
    );
  };

  // Render error state
  renderError = () => {
    const { error } = this.state;
    const { t } = this.props;

    return (
      <div className="error-container">
        <div className="error-content">
          <h3>⚠️ {t("common:error")}</h3>
          <p>{error}</p>
          <button className="retry-button" onClick={this.checkAndLoadData}>
            {t("common:retry")}
          </button>
        </div>
      </div>
    );
  };

  render() {
    const { childName, loading, error } = this.state;
    const { t, getChildrenFetching, getChildrenError } = this.props;

    // Show error if there's an error
    if (error) {
      return (
        <div className="progresssummary-page">
          <div className="progresssummary-container">
            <div className="page-header">
              <button className="back-button" onClick={this.back}>
                <ArrowLeftIcon className="back-icon" />
                {t("common:back")}
              </button>
              <h1 className="header-title">{t("progress-summary-screen:title")}</h1>
              <div className="header-right"></div>
            </div>
            {this.renderError()}
          </div>
        </div>
      );
    }

    // Show loading if fetching children or if local loading state is true
    if (getChildrenFetching || loading) {
      return (
        <div className="progresssummary-page">
          <div className="progresssummary-container">
            <div className="page-header">
              <button className="back-button" onClick={this.back}>
                <ArrowLeftIcon className="back-icon" />
                {t("common:back")}
              </button>
              <h1 className="header-title">{t("progress-summary-screen:title")}</h1>
              <div className="header-right"></div>
            </div>
            <div className="loading-container">
              <div className="loading-spinner">
                {getChildrenFetching ? 'Loading child data...' : 'Loading progress summary...'}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="progresssummary-page">
        <div className="progresssummary-container">
          {/* Header */}
          <div className="page-header">
            <div className="page-header-inner">
              <div className="header-left">
                <button className="header-button back-button" onClick={this.back}>
                  <ArrowLeftIcon className="back-icon" />
                </button>
                <h1 className="header-title">{childName}</h1>
              </div>
              <div className="header-body">
                
              </div>
              <div className="header-right"></div>
            </div>
          </div>

          {/* Main Content */}
          <div className="page-content">
            {/* Progress Summary Section */}
            <h2 className="section-header uppercase">
              {t("progress-summary-screen:section.title.progress-summary")}
            </h2>
            
            {/* Progress Chart - PHASE 4 IMPLEMENTED */}
            {this.renderProgressChart()}
            
            {/* Goal Attempts Section - PHASE 3 IMPLEMENTED */}
            {this.renderGoalAttempts()}

            
          </div>
        </div>
      </div>
    );
  }
}

// Redux connection - IMPLEMENTED IN PHASE 2
const mapStateToProps = (state: any) => ({
  currentSettings: state.currentSettings,
  children: GetChildrenSelectors.getChildren(state),
  getChildrenFetching: GetChildrenSelectors.isFetching(state),
  getChildrenSuccess: GetChildrenSelectors.isSuccess(state),
  getChildrenError: GetChildrenSelectors.hasError(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  getChildren: () => dispatch(GetChildrenActions.getChildrenRequest()),
  setCurrentChild: (child: any) => dispatch(CurrentSettingsActions.setCurrentChild(child)),
});

// Wrapper component to provide navigation props - PHASE 6 IMPLEMENTED
const ProgressSummaryPageWithNavigation: React.FC<WithTranslation> = (props) => {
  const navigate = (path: string) => {
    // Handle navigation using React Router
    if (typeof window !== 'undefined') {
      // Use React Router navigation if available
      if (window.history && window.history.pushState) {
        window.history.pushState({}, '', path);
        // Trigger route change event
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        // Fallback to location change
        window.location.href = path;
      }
    }
  };
  
  const goBack = () => {
    if (typeof window !== 'undefined') {
      if (window.history && window.history.length > 1) {
        window.history.back();
      } else {
        // Fallback to goals page
        navigate('/goals');
      }
    }
  };
  
  return (
    <ProgressSummaryPage 
      {...props}
      navigate={navigate}
      goBack={goBack}
    />
  );
};

// Export with all integrations - PHASES 4, 5, 6 COMPLETE
export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(ProgressSummaryPageWithNavigation));
